'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useRef, type MouseEvent, type ReactNode } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useMobileCategories } from './mobile/mobile-categories-state';

import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { useRouteOpen } from './mobile/use-route-open';

/** Figma „Overlay“ (370:61735): #0D110D at 50 %, no blur – over the header and the bottom bar. */
const OVERLAY = cn(
  'fixed inset-0 z-50 bg-[#0D110D]/50',
  'motion-safe:data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'motion-safe:data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
);

/*
 * Figma drawer (370:61735, 330 × 844): white, the full height from the left edge (also over the
 * bottom bar), padding 10, 24 px between the close button and the blocks. Narrow phones keep the
 * Figma 60 px of overlay on the right.
 */
const DRAWER = cn(
  'fixed inset-y-0 left-0 z-50 flex w-[330px] max-w-[calc(100vw-60px)] flex-col gap-6 overflow-y-auto overscroll-contain bg-white p-2.5 outline-none',
  'pb-[calc(10px+env(safe-area-inset-bottom))]',
  'motion-safe:data-[state=open]:animate-in data-[state=open]:slide-in-from-left',
  'motion-safe:data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left',
);

/**
 * Figma „Icons / Type=Menu“ (32 × 32): two round-capped 2 px lines, 22 px and 13.33 px long, from
 * x 5.33 at y 12 and 20.
 */
function MenuIcon() {
  return (
    <svg
      viewBox="0 0 32 32"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5.333 12h22M5.333 20h13.334" />
    </svg>
  );
}

/**
 * Figma „Open hamburger“ 370:61735 (below 1024 px): the menu icon of the mobile header row opens the
 * drawer from the left with the links and the contact, rendered on the server and passed as
 * children. Radix keeps the focus inside, Escape and the overlay close it and the focus returns to
 * the icon. Any link closes it – also the same-page anchors (/#sprievodca) that keep the route – and
 * so does a route change (browser back) while it is open.
 */
export function HeaderMobileMenu({
  children,
  primary,
  categoriesLabel,
}: {
  children: ReactNode;
  primary?: ReactNode;
  categoriesLabel?: string;
}) {
  const { t } = useTranslations();
  const { isOpen, setOpen } = useRouteOpen();
  const categories = useMobileCategories();
  const showCategoriesOnClose = useRef(false);

  // Click delegation only: every target inside is a link or a button with its own keys.
  const closeOnLink = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('a[href]')) setOpen(false);
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setOpen}>
      {/* 32 px like Figma; the invisible ::before makes the touch target 48 px. */}
      <DialogPrimitive.Trigger
        aria-label={t('text3241')}
        className="relative inline-flex size-8 shrink-0 items-center justify-center rounded-[8px] text-[#0D110D] before:absolute before:-inset-2 focus-visible:shadow-focus focus-visible:outline-none"
      >
        <MenuIcon />
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={cn(OVERLAY, 'lg:hidden')} />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onClick={closeOnLink}
          onCloseAutoFocus={event => {
            if (!showCategoriesOnClose.current) return;
            event.preventDefault();
            showCategoriesOnClose.current = false;
            categories.setOpen(true);
          }}
          className={cn(DRAWER, 'lg:hidden')}
        >
          <DialogPrimitive.Title className="sr-only">{t('text3241')}</DialogPrimitive.Title>
          <DialogPrimitive.Close asChild>
            <IconButton icon="x" label={t('text272')} variant="ghost" className="self-end" />
          </DialogPrimitive.Close>
          {primary}
          {categoriesLabel ? (
            <>
              <hr className="border-[#E2E5DF]" />
              <Button
                iconLeft="squares-four"
                className="w-full shrink-0"
                aria-haspopup="dialog"
                onClick={() => {
                  showCategoriesOnClose.current = true;
                  setOpen(false);
                }}
              >
                {categoriesLabel}
              </Button>
              <hr className="border-[#E2E5DF]" />
            </>
          ) : primary ? (
            <hr className="border-[#E2E5DF]" />
          ) : null}
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
